import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faChartLine } from "@fortawesome/pro-solid-svg-icons";
import { ExportButton, exportToExcelWithXLSX, ExportFieldCustomizer, SortableDataTable, } from "@perfectserve/library";
import { INTERACTION_EXPORT_FIELDS, EXPORT_FIELDS_CONFIG_VERSION } from "../types/interactions";
import SpeedToCare from "../components/SpeedToCare";
import InteractionDetailModal from "../components/InteractionDetailModal";
import CallFlowAnalysisModal from "../components/CallFlowAnalysisModal";
import { formatDuration, formatPhoneNumber, formatInteractionForDisplay } from "../utils/formatters";
import { formatInteractionDateTime } from "../utils/dateUtils";
import { calculateReadTime, calculateCallbackTime, calculateNotificationTime } from "../utils/interactionCalculations";
const InteractionList = ({ interactions, loading, onClose: _onClose, className = "", title = "Interaction Details", }) => {
    const [selectedInteraction, setSelectedInteraction] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedAnalysisInteraction, setSelectedAnalysisInteraction] = useState(null);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [showFieldCustomizer, setShowFieldCustomizer] = useState(false);
    const availableExportFields = React.useMemo(() => {
        return INTERACTION_EXPORT_FIELDS.map((field) => field.key);
    }, []);
    const handleFieldCustomizationSave = (_fields) => {
    };
    const handleExcelExport = async () => {
        try {
            const exportData = interactions.map((interaction) => {
                const formatted = formatInteractionForDisplay(interaction, formatInteractionDateTime);
                return {
                    ...formatted,
                    FromCallbackNumber: formatPhoneNumber(interaction.FromCallbackNumber),
                    FormattedStartDate: formatInteractionDateTime(interaction.InteractionStartDateUtc),
                    FormattedEndDate: interaction.InteractionEndDateUtc
                        ? formatInteractionDateTime(interaction.InteractionEndDateUtc)
                        : "—",
                    FormattedNotified: formatDuration(interaction.NotificationInSeconds),
                    FormattedReadTime: formatDuration(interaction.ReadInSeconds),
                    FormattedCallback: formatDuration(interaction.CallbackInSeconds),
                };
            });
            const savedConfig = localStorage.getItem("interaction-list-export-fields");
            let fieldConfig;
            if (savedConfig) {
                try {
                    fieldConfig = JSON.parse(savedConfig);
                }
                catch (_a) {
                    fieldConfig = undefined;
                }
            }
            await exportToExcelWithXLSX({
                filename: title.replace(/[^a-zA-Z0-9]/g, "_"),
                data: exportData,
                sheetName: "Interactions",
                fieldConfig,
            });
        }
        catch (error) {
            console.error("Excel export failed:", error);
            alert("Excel export failed. Please try again.");
        }
    };
    const handleViewInteractionDetail = (interaction) => {
        setSelectedInteraction(interaction);
        setShowDetailModal(true);
    };
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedInteraction(null);
    };
    const handleViewCallFlowAnalysis = (interaction) => {
        setSelectedAnalysisInteraction(interaction);
        setShowAnalysisModal(true);
    };
    const handleCloseAnalysisModal = () => {
        setShowAnalysisModal(false);
        setSelectedAnalysisInteraction(null);
    };
    const enhancedInteractions = useMemo(() => {
        return interactions.map((interaction) => {
            const notifiedResult = calculateNotificationTime(interaction);
            const readTime = calculateReadTime(interaction);
            const callbackTime = calculateCallbackTime(interaction);
            const storedTime = interaction.NotificationInSeconds;
            const hasDiscrepancy = notifiedResult.canCalculate &&
                storedTime !== undefined &&
                storedTime !== null &&
                Math.abs(notifiedResult.time - storedTime) > 1;
            return {
                ...interaction,
                formattedDate: formatInteractionDateTime(interaction.InteractionStartDateUtc),
                sortableDate: interaction.InteractionStartDateUtc ? new Date(interaction.InteractionStartDateUtc).getTime() : 0,
                calculatedNotifiedTime: notifiedResult.time,
                calculatedReadTime: readTime,
                calculatedCallbackTime: callbackTime,
                canCalculateNotified: notifiedResult.canCalculate,
                hasNotificationDiscrepancy: hasDiscrepancy,
            };
        });
    }, [interactions]);
    const columns = useMemo(() => [
        {
            key: "sortableDate",
            label: "Interaction Start Date",
            sortable: true,
            align: "left",
            width: "200px",
            render: (_, row) => row.formattedDate,
        },
        {
            key: "ToObjectName",
            label: "To",
            sortable: true,
            align: "left",
            width: "250px",
            render: (value) => value || "Unknown Recipient",
        },
        {
            key: "calculatedNotifiedTime",
            label: "Notified",
            sortable: true,
            align: "center",
            width: "100px",
            render: (_, row) => {
                if (!row.PushNotificationSentUtc) {
                    return "";
                }
                const displayValue = row.canCalculateNotified ? formatDuration(row.calculatedNotifiedTime) : "—";
                return (_jsx("span", { style: {
                        fontWeight: row.hasNotificationDiscrepancy ? "bold" : "normal",
                        color: row.hasNotificationDiscrepancy ? "red" : "inherit",
                    }, children: displayValue }));
            },
        },
        {
            key: "calculatedReadTime",
            label: "Read Time",
            sortable: true,
            align: "center",
            width: "100px",
            render: (_, row) => {
                if (row.SecureMessageReadUtc && row.SecureMessageSentUtc) {
                    return formatDuration(row.calculatedReadTime);
                }
                return "";
            },
        },
        {
            key: "calculatedCallbackTime",
            label: "Callback",
            sortable: true,
            align: "center",
            width: "100px",
            render: (_, row) => {
                if (row.CallbackFromMessageIdUtc && row.SecureMessageSentUtc) {
                    return formatDuration(row.calculatedCallbackTime);
                }
                return "";
            },
        },
        {
            key: "speedToCare",
            label: "Speed to Care",
            sortable: false,
            align: "center",
            width: "250px",
            render: (_, row) => (_jsx(SpeedToCare, { interaction: row, compact: true, showLegend: false, hideGraph: false })),
        },
        {
            key: "actions",
            label: "Actions",
            sortable: false,
            align: "center",
            width: "120px",
            render: (_, row) => (_jsxs("div", { style: {
                    display: "flex",
                    gap: "4px",
                    justifyContent: "center",
                }, children: [_jsx("button", { onClick: () => handleViewInteractionDetail(row), style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#374B94",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            transition: "background-color 0.2s",
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = "#f0f4ff";
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }, title: "View interaction details", children: _jsx(FontAwesomeIcon, { icon: faEye, size: "sm" }) }), _jsx("button", { onClick: () => handleViewCallFlowAnalysis(row), style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#28a745",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            transition: "background-color 0.2s",
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = "#f0fff4";
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }, title: "Analyze call flow", children: _jsx(FontAwesomeIcon, { icon: faChartLine, size: "sm" }) })] })),
        },
    ], []);
    if (loading) {
        return (_jsx("div", { className: `interactions-container ${className}`, style: { padding: "0" }, children: _jsx("div", { className: "d-flex justify-content-center align-items-center flex-column", style: {
                    height: "200px",
                    gap: "12px",
                }, children: _jsx("div", { className: "text-muted", style: { fontSize: "18px" }, children: "Loading interactions..." }) }) }));
    }
    return (_jsxs("div", { className: `interactions-container ${className}`, style: { padding: "0" }, children: [title && (_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-5 pl-15", children: [_jsxs("div", { className: "text-muted", style: {
                            fontSize: "16px",
                            fontWeight: "500",
                        }, children: ["(", interactions.length, " interactions)"] }), _jsx(ExportButton, { title: title.replace(/[^a-zA-Z0-9]/g, "_"), onExcelExport: handleExcelExport, exportElementId: "interaction-list-content", variant: "links", buttonStyle: { fontSize: "12px" }, enableFieldCustomization: true, availableFields: availableExportFields, onFieldCustomizationOpen: () => setShowFieldCustomizer(true), hidePdfExport: true })] })), _jsx("div", { id: "interaction-list-content", style: { margin: "0" }, children: enhancedInteractions.length === 0 ? (_jsx("div", { className: "text-center text-muted p-40", style: { fontSize: "16px" }, children: "No interactions found" })) : (_jsx("div", { style: {
                        border: "1px solid var(--ps-gray4)",
                        borderRadius: "var(--ps-border-radius)",
                        overflow: "auto",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        margin: "0",
                        height: "430px",
                        backgroundColor: "white",
                    }, children: _jsx(SortableDataTable, { columns: columns, data: enhancedInteractions, defaultSortKey: "sortableDate", defaultSortDirection: "desc", emptyMessage: "No interactions found", getRowId: (row) => row.Id }) })) }), _jsx(InteractionDetailModal, { interaction: selectedInteraction, open: showDetailModal, onClose: handleCloseDetailModal }), _jsx(CallFlowAnalysisModal, { interaction: selectedAnalysisInteraction, open: showAnalysisModal, onClose: handleCloseAnalysisModal }), _jsx(ExportFieldCustomizer, { isOpen: showFieldCustomizer, onClose: () => setShowFieldCustomizer(false), availableFields: availableExportFields, storageKey: "interaction-list-export-fields", onSave: handleFieldCustomizationSave, sampleData: interactions.slice(0, 10), fieldDefinitions: INTERACTION_EXPORT_FIELDS, configVersion: EXPORT_FIELDS_CONFIG_VERSION })] }));
};
export default InteractionList;
//# sourceMappingURL=InteractionList.js.map